import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../config/axios';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { useHandleCheck, HandleFeedback, HANDLE_HINT } from '../components/HandleChooser';
import { signInFailureText } from '../utils/loadFailure';
import { signInReason } from '../utils/signInReason';

// Password bounds mirror the API's (server/routes/auth.js) so most mistakes
// are caught client-side; the API remains the authority.
const PASSWORD_MIN = 8;

// Map the API's deliberately generic error codes to friendly copy. The API
// never says "email already exists" (anti-enumeration) — neither do we.
const API_ERRORS = {
  invalid_email: 'Please enter a valid email address.',
  invalid_password: `Password must be at least ${PASSWORD_MIN} characters.`,
  registration_failed: 'Registration failed. Try a different email or log in instead.',
};

// The server's answers to a missing or wrong access key (issue 1229); its own words are shown.
const KEY_REFUSALS = ['key_required', 'wrong_key', 'too_many'];

const Register = () => {
  const { register, claimHandle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  // The handle is asked for at signup (issue 500 §1) and is OPTIONAL: a failed
  // handle must never cost someone their account, so registration completes
  // first and the claim follows; if the claim fails the prompt simply returns
  // on the next page. The live preview below is the confirm step — the
  // handle as it will appear, with the permanence said plainly.
  const [handle, setHandle] = useState('');
  const handleCheck = useHandleCheck(handle);
  // The development site asks for the access key (issue 1229). The site says
  // whether to show the box; the server enforces it either way.
  const [keyRequired, setKeyRequired] = useState(false);
  const [accessKey, setAccessKey] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let live = true;
    // nocache: the service worker must not keep this answer.
    apiClient.get('/api/auth/signup-config', { params: { nocache: 1 } })
      .then((res) => { if (live && res.data?.keyRequired === true) setKeyRequired(true); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (password.length < PASSWORD_MIN) {
      setError(`Password must be at least ${PASSWORD_MIN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (handle && !(handleCheck.status === 'ok' && handleCheck.handle === handle)) {
      setError(handleCheck.status === 'bad' ? handleCheck.message : 'Wait a moment for the handle check, or clear the handle.');
      return;
    }
    if (keyRequired && !accessKey.trim()) {
      setError('Enter the access key.');
      return;
    }

    setSubmitting(true);
    try {
      await register(email.trim(), password, keyRequired ? accessKey.trim() : undefined);
      if (handle) {
        try { await claimHandle(handle); } catch (e) { /* the prompt returns on the next page */ }
      }
      navigate(from, { replace: true });
    } catch (err) {
      const code = err.response?.data?.error;
      if (KEY_REFUSALS.includes(code)) {
        setKeyRequired(true);
        setError(err.response.data.reason);
      } else {
        setError(API_ERRORS[code] || signInFailureText(err) || 'Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign up — The Dragon's Table</title>
        <meta name="description" content="Create a free Dragon's Table account to save your favorite spells, feats, monsters, and more." />
      </Helmet>

      <div className="max-w-sm mx-auto space-y-6">
        <div className="card">
          <h1 className="section-header flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            <span>Sign up</span>
          </h1>

          {signInReason(from) && (
            <p className="text-sm mt-3" role="note" data-testid="sign-in-reason" style={{ color: 'var(--text-secondary)' }}>
              {signInReason(from)}
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 mt-4" noValidate>
            <div>
              <label htmlFor="register-email" className="form-label">Email</label>
              <input
                id="register-email"
                type="email"
                className="input"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="register-password" className="form-label">Password</label>
              <input
                id="register-password"
                type="password"
                className="input"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                At least {PASSWORD_MIN} characters.
              </p>
            </div>

            <div>
              <label htmlFor="register-confirm" className="form-label">Confirm password</label>
              <input
                id="register-confirm"
                type="password"
                className="input"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="register-handle" className="form-label">Handle <span style={{ color: 'var(--text-muted)' }}>(optional)</span></label>
              <div className="flex items-center gap-1">
                <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>@</span>
                <input
                  id="register-handle"
                  type="text"
                  className="input flex-1"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={30}
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  aria-describedby="register-handle-hint"
                />
              </div>
              <p id="register-handle-hint" className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{HANDLE_HINT}</p>
              <HandleFeedback check={handleCheck} value={handle} />
            </div>

            {keyRequired && (
              <div>
                <label htmlFor="register-access-key" className="form-label">Access key</label>
                <input
                  id="register-access-key"
                  type="password"
                  className="input"
                  autoComplete="off"
                  spellCheck={false}
                  value={accessKey}
                  onChange={(e) => setAccessKey(e.target.value)}
                />
              </div>
            )}

            {error && <p className="form-error" role="alert">{error}</p>}

            <button type="submit" className="btn btn-primary w-full" disabled={submitting}>
              {submitting ? 'Creating account…' : 'Create account'}
            </button>
          </form>

          <div className="mt-6 pt-4 space-y-3" style={{ borderTop: '1px solid var(--border)' }}>
            <GoogleSignInButton
              onSuccess={() => navigate(from, { replace: true })}
              onError={(message) => setError(message)}
            />
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Already have an account?{' '}
              <Link to="/login" state={{ from }} className="footer-link" style={{ color: 'var(--link)' }}>
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
